// src/services/fcmService.js

import { getToken, onMessage, isSupported } from 'firebase/messaging';
import { doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getMessagingInstance, firestore, firebaseConfig } from '../firebase/firebase';
import Axios from '../actions/Axios';

export const ADMIN_FCM_TOKENS_COLLECTION = 'admin_fcm_tokens';

const VAPID_KEY =
  process.env.REACT_APP_FIREBASE_VAPID_KEY ||
  'BMfPdXv9782XomzokmtvcC--vzi71ZcV576-fn0_cDgdsjWxDeDwPYeV2VamDtjoguVkIROouqj4iGjGbQYSlRQ';

/**
 * Checks if the current browser environment supports Push Notifications, Service Workers,
 * and has the required Firebase App ID configured.
 *
 * @returns {Promise<boolean>}
 */
export const isNotificationSupported = async () => {
  if (typeof window === 'undefined') return false;
  if (!('Notification' in window)) return false;
  if (!('serviceWorker' in navigator)) return false;
  if (!firebaseConfig?.appId) return false;

  try {
    return await isSupported();
  } catch (e) {
    return false;
  }
};

/**
 * Returns the current Notification permission state ('default' | 'granted' | 'denied').
 *
 * @returns {string}
 */
export const getNotificationPermission = () => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
};

/**
 * Registers the firebase-messaging-sw.js Service Worker if not already registered.
 *
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export const registerServiceWorker = async () => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }

  try {
    const swUrl = '/firebase-messaging-sw.js';
    let registration = await navigator.serviceWorker.getRegistration('/');
    if (!registration) {
      registration = await navigator.serviceWorker.register(swUrl, {
        scope: '/',
      });
    }
    // Wait for the service worker to become active and ready
    const readyReg = await navigator.serviceWorker.ready;
    return readyReg && readyReg.pushManager
      ? readyReg
      : registration && registration.pushManager
        ? registration
        : null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[FCM] Service Worker registration failed:', error);
    return null;
  }
};

/**
 * Requests browser notification permission, retrieves FCM web token,
 * and saves it into Firestore under admin_fcm_tokens/{token}.
 *
 * @returns {Promise<{ token: string|null, permission: string }>}
 */
export const requestNotificationPermissionAndGetToken = async () => {
  if (!firebaseConfig?.appId) {
    // eslint-disable-next-line no-console
    console.warn(
      "[FCM] Cannot request token: 'REACT_APP_FIREBASE_APP_ID' is missing in your .env file."
    );
    return {
      token: null,
      permission: 'unsupported',
      error: 'Missing REACT_APP_FIREBASE_APP_ID in .env',
    };
  }

  const supported = await isNotificationSupported();
  if (!supported) {
    return { token: null, permission: 'unsupported' };
  }

  try {
    // 1. Request permission from user
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return { token: null, permission };
    }

    // 2. Ensure Service Worker is registered
    const swRegistration = await registerServiceWorker();
    const messaging = await getMessagingInstance();

    if (!messaging) {
      throw new Error('Firebase Messaging instance is unavailable.');
    }

    // 3. Get FCM Web Push Token using VAPID key
    const tokenOptions = { vapidKey: VAPID_KEY };
    if (swRegistration && swRegistration.pushManager) {
      tokenOptions.serviceWorkerRegistration = swRegistration;
    }

    const token = await getToken(messaging, tokenOptions);

    if (!token) {
      throw new Error('Failed to retrieve FCM token from Firebase.');
    }

    // 4. Save token to Firestore for multi-admin push dispatch
    if (firestore) {
      const adminId = localStorage.getItem('_id') || '60796b3cccefb0dc1fac984c';
      const adminName = localStorage.getItem('name') || 'Support Admin';
      const tokenRef = doc(firestore, ADMIN_FCM_TOKENS_COLLECTION, token);

      await setDoc(
        tokenRef,
        {
          token,
          adminId,
          adminName,
          platform: 'web',
          userAgent: navigator.userAgent,
          active: true,
          updatedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
        },
        { merge: true }
      );
    }

    // 5. Store current device token in localStorage
    localStorage.setItem('admin_fcm_token', token);

    // 6. Sync token with MongoDB users table
    await syncAdminTokenToBackend(token);

    return { token, permission: 'granted' };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[FCM] Error requesting notification token:', error);
    return { token: null, permission: Notification.permission, error };
  }
};

/**
 * Syncs the FCM token to the backend users table in MongoDB for the current admin.
 *
 * @param {string} token FCM registration token
 */
export const syncAdminTokenToBackend = async (token) => {
  try {
    const adminId = localStorage.getItem('_id');
    const authToken = localStorage.getItem('token');
    if (!adminId && !authToken) {
      // eslint-disable-next-line no-console
      console.log(
        '[FCM] No active admin session yet; token stored in localStorage for login sync.'
      );
      return;
    }
    const response = await Axios.post('/admin/updateFirebaseToken', {
      firebaseToken: token || '',
      adminId: adminId,
    });
    // eslint-disable-next-line no-console
    console.log('[FCM] Token synced to backend users table:', response?.data);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(
      '[FCM] Failed to sync FCM token to backend users table:',
      error?.response?.data || error?.message || error
    );
  }
};

/**
 * Marks the current browser's FCM token as inactive in Firestore and clears it from MongoDB users table upon admin sign out.
 */
export const removeAdminTokenFromFirestore = async () => {
  const currentToken = localStorage.getItem('admin_fcm_token');
  const adminId = localStorage.getItem('_id');

  if (currentToken && firestore) {
    try {
      const tokenRef = doc(firestore, ADMIN_FCM_TOKENS_COLLECTION, currentToken);
      await updateDoc(tokenRef, {
        active: false,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[FCM] Error deactivating admin token in Firestore:', e);
    }
  }

  if (adminId) {
    try {
      await Axios.post('/admin/updateFirebaseToken', {
        firebaseToken: '',
        adminId: adminId,
      });
      // eslint-disable-next-line no-console
      console.log('[FCM] Cleared FCM token from backend users table.');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[FCM] Error clearing admin token in backend:', e);
    }
  }

  localStorage.removeItem('admin_fcm_token');
};

/**
 * Subscribes to foreground push messages while the admin panel tab is open and active.
 *
 * @param {Function} callback Callback receiving the push payload
 * @returns {Promise<Function>} Unsubscribe function
 */
export const onForegroundMessage = async (callback) => {
  const supported = await isNotificationSupported();
  if (!supported) return () => {};

  try {
    const messaging = await getMessagingInstance();
    if (!messaging) return () => {};

    return onMessage(messaging, callback);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[FCM] Failed to attach onMessage listener:', error);
    return () => {};
  }
};
